"use client";

import { Button } from "@/components/rac/button";
import { Card } from "@/components/rac/card";
import { Input } from "@/components/rac/input";
import { REQUEST_CALLBACK } from "@/gql/unit-and-project";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Country, getCountriesWithPhoneCodes, getDefaultCountry } from "@/info/countries";
import { formatPhoneNumber, isValidPhoneNumber } from "@/info/login";
import { trackCallbackForProject, trackCallbackForUnit } from "@/utils/analytics/track-callback";
import { logger } from "@/utils/logger";
import { utmUtils } from "@/utils/utm";
import { useMutation } from "@apollo/client";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
interface CallbackRequestModalProps {
  open: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  userPhoneNumbers?: string[];
  projectId?: string;
  unitId?: string;
  rawUnitId?: string;
}
export const CallbackRequestModal: React.FC<CallbackRequestModalProps> = ({
  open,
  onClose,
  isAuthenticated,
  userPhoneNumbers,
  projectId,
  unitId,
  rawUnitId
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [phone, setPhone] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country>(getDefaultCountry());
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [requestCallbackMutation, {
    loading: isLoading
  }] = useMutation(REQUEST_CALLBACK);
  const [submitting, setSubmitting] = useState(false);
  React.useEffect(() => {
    if (open) {
      setPhone("");
      setSelectedPhone(null);
      setStep(0);
      setSelectedCountry(getDefaultCountry());
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  // Handle escape key press
  React.useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        event.preventDefault();
        onClose();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [open, onClose]);
  if (!open) return null;
  const submitCallbackRequest = async (phoneNumber: string) => {
    setSubmitting(true);
    try {
      const variables = {
        filters: {
          requestCallback: {
            phone: phoneNumber,
            ...(projectId && {
              projectId
            }),
            ...(unitId && {
              unitId
            }),
            ...(rawUnitId && {
              rawUnitId
            }),
            url: window.location.pathname,
            info: {
              utm: utmUtils.getCurrent()
            }
          }
        }
      };
      const {
        data: res,
        errors
      } = await requestCallbackMutation({
        variables
      });
      const result = res?.unitMethods?.requestCallback;
      if (result) {
        if (unitId) {
          trackCallbackForUnit(unitId, {
            phoneNumber
          });
        } else if (projectId) {
          trackCallbackForProject(projectId, {
            phoneNumber
          });
        }
        logger.info("Callback requested for", phoneNumber);
        toast.success("Callback requested successfully", {
          duration: 3000,
          position: "bottom-right"
        });
        onClose();
      } else {
        logger.error("Callback Request Failed", {
          result,
          res,
          errors
        });
        toast.error("Callback request failed", {
          description: "Please try again later",
          duration: 5000,
          position: "bottom-right"
        });
      }
    } catch (e) {
      logger.error(e);
      toast.error("Could not request callback", {
        description: "Please try again later",
        duration: 5000,
        position: "bottom-right"
      });
    } finally {
      setSubmitting(false);
    }
  };
  const isBusy = isLoading || submitting;

  // Helper: phone input UI
  const phoneInput = <form className="flex flex-col gap-4" onSubmit={e_0 => {
    e_0.preventDefault();
    if (!phone || !isValidPhoneNumber(phone, selectedCountry.code)) {
      setError("Please enter a valid phone number");
      return;
    }
    setError("");
    submitCallbackRequest(formatPhoneNumber(phone, selectedCountry.code));
  }} {...isBusy ? {
    "aria-busy": "true"
  } : {}}>
			<label className="font-medium text-foreground">Enter your phone number:</label>
			<div className="flex gap-2 items-center">
				<select title="Select country code" value={selectedCountry.countryCode} onChange={e_1 => {
        const code = e_1.target.value;
        const found = getCountriesWithPhoneCodes().find((c: Country) => c.countryCode === code);
        if (found) setSelectedCountry(found);
      }} className="h-9 min-w-[70px] max-w-[110px] text-xs rounded-md border border-input bg-background px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all">
					{getCountriesWithPhoneCodes().map((c_0: Country) => <option key={c_0.countryCode} value={c_0.countryCode}>
							{c_0.flag} {c_0.countryCode} {c_0.code}
						</option>)}
				</select>
				<Input type="tel" value={phone} onChange={e_2 => {
        setPhone(e_2.target.value.replace(/[^\d]/g, ""));
        if (e_2.target.value.length === 10) setError("");else setError("Please enter a valid phone number");
      }} placeholder="Phone number" className="flex-1" autoComplete="tel" inputMode="numeric" maxLength={10} />
			</div>
			<span className="text-destructive text-sm h-5">{error ? error : "\u00A0"}</span>
			<Button type="submit" className="w-full" variant="default" isDisabled={isBusy || error !== "" || phone.length !== 10} {...isBusy ? {
      "aria-busy": "true"
    } : {}}>
				{isBusy ? <>
						<Loader2 className="w-4 h-4 mr-2 animate-spin" />
						Requesting...
					</> : <>Request Callback</>}
			</Button>
		</form>;

  // Helper: confirmation UI
  const confirmUI = (number: string) => <div className="flex flex-col gap-4">
			<div>
				Request callback on <span className="font-semibold">{number}</span>?
			</div>
			<div className="flex justify-between w-full">
				<Button variant="default" onClick={() => submitCallbackRequest(number)} isDisabled={isBusy} {...isBusy ? {
        "aria-busy": "true"
      } : {}}>
					{isBusy ? <>
							<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							Requesting...
						</> : <>Yes, Request</>}
				</Button>
				<Button variant="ghost" onClick={onClose} className="border hover:bg-muted/50 hover:border-border">
					Cancel
				</Button>
			</div>
		</div>;

  // Helper: select phone UI
  const selectPhoneUI = <div className="flex flex-col gap-4">
			<div>Select a phone number for callback:</div>
			<div className="flex flex-col gap-2">
				{userPhoneNumbers?.map(num => <div key={num} className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${selectedPhone === num ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border hover:border-primary/40 hover:bg-primary/2"}`} onClick={() => setSelectedPhone(num)}>
						<div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${selectedPhone === num ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
							{selectedPhone === num && <div className="w-2 h-2 rounded-full bg-white"></div>}
						</div>
						<span className="text-sm font-medium">{num}</span>
					</div>)}
			</div>
			<Button variant="default" className="mt-2" isDisabled={!selectedPhone} onClick={() => selectedPhone && setStep(2)}>
				Continue
			</Button>
		</div>;

  // Modal content logic
  let content;
  if (!isAuthenticated) {
    content = phoneInput;
  } else if (!userPhoneNumbers || userPhoneNumbers.length === 0) {
    content = phoneInput;
  } else if (userPhoneNumbers.length === 1) {
    content = confirmUI(userPhoneNumbers[0]);
  } else if (userPhoneNumbers.length > 1) {
    if (step === 0) {
      content = selectPhoneUI;
    } else if (step === 2 && selectedPhone) {
      content = confirmUI(selectedPhone);
    }
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-background/80 to-primary/10 backdrop-blur-sm" onClick={onClose}>
			<Card className={`relative border border-primary/10 shadow-2xl p-8 ${isDesktop ? "min-w-[400px] max-w-xl" : "min-w-[340px] max-w-[95dvw]"} animate-fadeIn`} onClick={e_3 => e_3.stopPropagation()}>
				<Button onClick={onClose} variant="ghost" className="absolute top-4 right-4 text-2xl font-light" aria-label="Close">
					&times;
				</Button>
				<div className="flex flex-col items-center mb-4">
					<h2 className="text-xl font-bold text-primary mb-1">Request a Callback</h2>
					<p className="text-muted-foreground text-sm">We&apos;ll get in touch with you soon!</p>
				</div>
				{content}
			</Card>
		</div>;
};
export default CallbackRequestModal;